import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

// Helper to dynamically load Tesseract.js from CDN
const loadTesseract = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    if ((window as any).Tesseract) {
      resolve((window as any).Tesseract);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5.0.3/dist/tesseract.min.js';
    script.onload = () => {
      if ((window as any).Tesseract) {
        resolve((window as any).Tesseract);
      } else {
        reject(new Error('Tesseract failed to load from window'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Tesseract.js script'));
    document.head.appendChild(script);
  });
};

interface ScaleScreenshotParserProps {
  onParsed: (weight: number, bodyFat: number | undefined, notes: string) => void;
}

export default function ScaleScreenshotParser({ onParsed }: ScaleScreenshotParserProps) {
  const [isParsing, setIsParsing] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setIsParsing(true);

    // Create local object URL for preview
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);

    try {
      // 1. Load Tesseract.js
      const Tesseract = await loadTesseract();

      // 2. Perform OCR
      const result = await Tesseract.recognize(
        file,
        'eng',
        { 
          logger: (m: any) => console.log(m) 
        }
      );

      const text = result.data.text;
      console.log("OCR Extracted Text:", text);

      // 3. Parse Renpho scale metrics using regex
      // Weight pattern in Renpho is usually like "149.2 lb" or "149.2" followed by "lb"
      // Let's search for numbers followed by lb, lbs, or on their own
      const lines = text.split('\n');
      let weight: number | null = null;
      let bodyFat: number | null = null;

      // Regex matching:
      // Weight: Match "149.2" or "149 . 2" (sometimes OCR adds spaces around dots)
      // Body Fat: Match "11.8%" or "11.8 %"
      const weightRegex = /(\d{2,3})[\s.]+(\d)\s*(?:lb|lbs|1b|1bs)?/i;
      const bodyFatRegex = /(\d{1,2})[\s.]+(\d)\s*%/;

      // Iterate through lines to locate metrics
      for (const line of lines) {
        const cleanLine = line.replace(/\s+/g, ' ').trim();
        
        // Try to match weight
        if (!weight) {
          const match = cleanLine.match(weightRegex);
          if (match) {
            // Reconstruct the decimal weight (e.g. "149" and "2" -> 149.2)
            const val = parseFloat(`${match[1]}.${match[2]}`);
            if (val > 80 && val < 400) { // Valid human weight range
              weight = val;
            }
          }
        }

        // Try to match body fat
        if (!bodyFat) {
          const match = cleanLine.match(bodyFatRegex);
          if (match) {
            const val = parseFloat(`${match[1]}.${match[2]}`);
            if (val > 2 && val < 60) { // Valid body fat range
              bodyFat = val;
            }
          }
        }
      }

      // Fallback parser if regex line search missed it
      if (!weight) {
        // Search globally in the text for "XX.X lb" or "XXX.X lb"
        const globalWeightMatch = text.match(/(\d{2,3})[\s.]+(\d)\s*(?:lb|lbs|1b|1bs)/i);
        if (globalWeightMatch) {
          weight = parseFloat(`${globalWeightMatch[1]}.${globalWeightMatch[2]}`);
        }
      }

      if (!bodyFat) {
        const globalFatMatch = text.match(/(\d{1,2})[\s.]+(\d)\s*%/);
        if (globalFatMatch) {
          bodyFat = parseFloat(`${globalFatMatch[1]}.${globalFatMatch[2]}`);
        }
      }

      // 4. Handle results
      if (weight) {
        let note = `Synced via Scale Screenshot (${file.name})`;
        if (bodyFat) {
          note += ` • Body Fat: ${bodyFat}%`;
        }

        toast.success(`Smart Scale Sync: Detected ${weight} lbs!${bodyFat ? ` (Body Fat: ${bodyFat}%)` : ''}`, {
          icon: <Sparkles className="h-4 w-4 text-primary" />,
          duration: 6000
        });

        onParsed(weight, bodyFat || undefined, note);
      } else {
        // If OCR failed to find a valid weight, fall back to manual entry but notify user
        toast.error("Could not read weight from screenshot. Please enter it manually below.", {
          duration: 5000
        });
      }
    } catch (err) {
      console.error("OCR Error:", err);
      toast.error("Failed to parse screenshot. Please enter your weight manually.");
    } finally {
      setIsParsing(false);
    }
  };

  const handleClearImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFileName(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="border border-dashed border-border/60 rounded-sm p-4 bg-background/20 text-center space-y-3 relative overflow-hidden group hover:border-primary/50 transition-colors">
      <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      
      <div className="flex flex-col items-center justify-center">
        {isParsing ? (
          <Loader2 className="h-8 w-8 text-primary animate-spin mb-2" />
        ) : imagePreview ? (
          <div className="relative mb-2 w-16 h-16 rounded border border-border overflow-hidden group/preview">
            <img src={imagePreview} alt="Scale Screenshot" className="w-full h-full object-cover" />
            <button 
              onClick={handleClearImage}
              className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover/preview:opacity-100 transition-opacity cursor-pointer text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <Upload className="h-8 w-8 text-muted-foreground/50 mb-2 group-hover:text-primary transition-colors" />
        )}
        
        <span className="block text-xs font-mono text-muted-foreground uppercase">
          {isParsing ? "PARSING SCALE OCR..." : imagePreview ? "SCREENSHOT LOADED" : "SYNC SMART SCALE SCREENSHOT"}
        </span>
        <span className="block text-[9px] text-muted-foreground/70 mt-1 uppercase">
          {imagePreview ? "Weight extracted automatically" : "Drag & drop or click to upload screenshot"}
        </span>
      </div>

      <div className="flex justify-center">
        <Button
          type="button"
          variant="outline"
          disabled={isParsing}
          onClick={handleButtonClick}
          className="h-7 px-3 font-mono text-[10px] border-primary/40 text-primary hover:bg-primary/10"
        >
          {fileName ? "CHANGE SCREENSHOT" : "SELECT SCREENSHOT"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={isParsing}
          className="hidden"
        />
      </div>
    </div>
  );
}
